import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import useSeo from "@/hooks/useSeo";
import { siteMeta } from "@/mocks/comics";

const values = [
  {
    icon: "ri-compass-3-line",
    title: "清晰的选书入口",
    desc: "用榜单和分类，帮你快速定位想看的韩漫，而不是在一堆信息里迷失。",
  },
  {
    icon: "ri-eye-line",
    title: "真实的口碑依据",
    desc: "以读者打分为核心依据，高分榜单代表多数人的真实喜好，选书更放心。",
  },
  {
    icon: "ri-refresh-line",
    title: "持续更新的内容",
    desc: "人气榜单与完结漫画分类保持每日更新，新番上线即上榜，跟上追漫节奏。",
  },
  {
    icon: "ri-heart-line",
    title: "以读者为中心",
    desc: "所有内容都围绕「帮读者高效找到好作品」展开，不堆砌、不打扰。",
  },
];

const reviews = [
  {
    name: "阿澈",
    tag: "完结漫画爱好者",
    text: "一直有书荒的毛病，现在每天来榜单看一眼，很快就能找到想追的完结作品，真的省心很多。",
  },
  {
    name: "小夏",
    tag: "恋爱题材党",
    text: "分类做得特别清楚，恋爱题材的高分作品一找一个准，评分也比较符合我的口味。",
  },
  {
    name: "老白",
    tag: "新番追更族",
    text: "新番抢先看这个板块很赞，好几部喜欢的作品都是在这里最早发现的，赶上了更新节奏。",
  },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 韩漫YY榜 品牌介绍与发展理念",
    description:
      "了解韩漫YY榜：一个以清晰榜单与完善分类为核心，帮助读者高效找到好看韩漫的内容平台。阅读我们的网站定位、发展理念与内容原则。",
    keywords: "韩漫YY榜,关于我们,韩漫榜单平台,韩漫推荐",
    path: "/about",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于韩漫YY榜",
      description: "韩漫YY榜的品牌介绍、发展理念与内容原则。",
      url: `${window.location.origin}/about`,
      mainEntity: {
        "@type": "Organization",
        name: siteMeta.name,
        slogan: siteMeta.slogan,
        email: siteMeta.email,
      },
    },
  });

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-16 md:pt-20">
        <section className="relative overflow-hidden bg-foreground-950">
          <div className="absolute inset-0 opacity-25">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20warm%20illustration%20of%20flying%20open%20books%20and%20colorful%20comic%20panels%20in%20coral%20and%20golden%20light%2C%20stylized%20art%2C%20inspirational%20editorial%20background%2C%20high%20detail%2C%20cinematic%20composition&width=1600&height=700&seq=about-hero-01&orientation=landscape"
              alt="关于韩漫YY榜"
              className="h-full w-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 to-foreground-950/60"></div>
          <div className="relative z-10 mx-auto max-w-[1200px] px-4 md:px-6 py-16 md:py-24 text-background-50">
            <p className="text-sm font-medium text-accent-400">ABOUT US</p>
            <h1 className="mt-3 font-heading text-3xl md:text-5xl">让每一本好韩漫，都不被错过</h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
              {siteMeta.name}是一个专注于韩漫推荐的榜单平台。我们相信，找书不该是一件费力的事——
              用清晰的人气榜单、完善的题材分类和真实的口碑评分，帮你快速找到想看的作品。
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-[1200px] px-4 md:px-6 py-14 md:py-20">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-3xl text-foreground-950">我们是谁</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                {siteMeta.name}诞生于一个很朴素的想法：追韩漫的乐趣在于沉浸故事，而不该被「找什么好看」这件事消耗掉。所以我们把全部精力放在三件事上——把人气榜单做得清楚、把题材分类做得完善、把评分口碑做得可信。
              </p>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                我们不做庞杂的信息堆砌，只保留对读者真正有用的部分。无论你是追更老手还是刚入坑的新人，在这里都能用最短的时间，锁定下一本让你熬夜追完的好作品。
              </p>
            </div>
            <div className="flex flex-col gap-4">
              {[
                { icon: "ri-eye-line", label: "网站定位", value: "韩漫推荐榜单平台" },
                { icon: "ri-compass-line", label: "发展理念", value: "让找书更高效" },
                { icon: "ri-check-double-line", label: "内容原则", value: "真实、清晰、实用" },
              ].map((it) => (
                <div key={it.label} className="flex items-center gap-4 rounded-lg bg-background-100 p-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className={`${it.icon} text-lg`}></i>
                  </span>
                  <div>
                    <div className="text-xs text-foreground-600">{it.label}</div>
                    <div className="text-sm font-semibold text-foreground-950">{it.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-14 md:py-20">
          <div className="mx-auto max-w-[1200px] px-4 md:px-6">
            <h2 className="font-heading text-3xl text-foreground-950">我们坚持什么</h2>
            <p className="mt-2 max-w-xl text-sm text-foreground-600">四条内容原则，贯穿榜单、分类与推荐的每一个环节。</p>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {values.map((v) => (
                <div key={v.title} className="card-lift rounded-lg bg-background-50 p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <i className={`${v.icon} text-lg`}></i>
                  </span>
                  <h3 className="mt-4 text-base font-semibold text-foreground-950">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1200px] px-4 md:px-6 py-14 md:py-20">
          <h2 className="font-heading text-3xl text-foreground-950">读者怎么说</h2>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {reviews.map((r) => (
              <figure key={r.name} className="rounded-lg bg-background-100 p-6">
                <div className="flex items-center gap-1 text-accent-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <blockquote className="mt-4 text-sm leading-relaxed text-foreground-700">“{r.text}”</blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 font-heading text-primary-700">
                    {r.name[0]}
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-foreground-950">{r.name}</div>
                    <div className="text-xs text-foreground-600">{r.tag}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-[1200px] px-4 md:px-6 pb-16 md:pb-20">
          <div className="rounded-lg bg-primary-500 px-6 py-10 md:px-10 md:py-12 text-center text-background-50">
            <h2 className="font-heading text-2xl md:text-3xl">和更多读者一起追好漫</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-background-100">
              从人气榜单开始，找到你的下一本心头好。有任何建议或反馈，欢迎通过邮箱联系我们。
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/#ranking" className="w-full whitespace-nowrap rounded-md bg-background-50 px-7 py-3 text-sm font-medium text-primary-700 transition-colors hover:bg-background-100 sm:w-auto">
                查看人气榜单
              </Link>
              <a href={`mailto:${siteMeta.email}`} className="w-full whitespace-nowrap rounded-md border border-background-50/40 px-7 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:w-auto">
                联系我们
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}